import { motion } from 'framer-motion';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="terms-of-service-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home">
            <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold">Protego AI</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-gray-400 mb-12">Protego AI Inc. | section3.ai | Effective: March 2, 2026</p>

          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms of Service ("Terms") govern your access to and use of the website section3.ai and the Protego AI account health monitoring platform provided by Protego AI Inc. ("we," "us," or "our"). By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service. These Terms constitute a legally binding agreement between you and Protego AI Inc.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Protego AI is an account health monitoring and risk intelligence platform designed to help Amazon sellers understand and proactively manage enforcement risks associated with their Amazon seller accounts. Our Service provides:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Risk scoring based on analysis of account health metrics obtained through the Amazon Selling Partner API</li>
                <li>Early warning signals and alerts for potential policy violations</li>
                <li>Educational content and insights regarding Amazon's enforcement patterns</li>
                <li>Account health audits and advisory reports</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                The Service is intended for informational and educational purposes only. We do not provide legal advice, and nothing in our Service constitutes a guarantee of any specific outcome with respect to your Amazon seller account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Eligibility</h2>
              <p className="text-gray-300 leading-relaxed">
                You must be at least 18 years of age to use our Service. By using our Service, you represent and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms. If you are using the Service on behalf of a business entity, you represent and warrant that you have the authority to bind that entity to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Account Registration</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                To access certain features of the Service, you may be required to register for an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password and account credentials confidential</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                We reserve the right to suspend or terminate your account if we suspect any unauthorized use or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Amazon SP-API Authorization</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                To use the core monitoring features of our Service, you must authorize us to access your Amazon seller data through the Amazon Selling Partner API. By granting this authorization, you:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Confirm that you are the authorized Amazon seller account holder or have permission to authorize SP-API access</li>
                <li>Agree that we may access the data categories specified during the authorization process</li>
                <li>Acknowledge that you may revoke SP-API access at any time through your Amazon Seller Central settings</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                You are solely responsible for ensuring that your use of our Service complies with Amazon's Terms of Service, Selling Policies, and any applicable agreements with Amazon.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Acceptable Use</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Use the Service for any unlawful purpose or in violation of any applicable regulations</li>
                <li>Attempt to circumvent, hack, or interfere with any security features of the Service</li>
                <li>Scrape, copy, or redistribute any content from the Service without our express written consent</li>
                <li>Use the Service to engage in any conduct that could harm, disrupt, or overburden our infrastructure</li>
                <li>Misrepresent your identity or affiliation with any person or organization</li>
                <li>Use the Service to develop a competing product or service</li>
                <li>Share your account credentials with any third party</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Intellectual Property</h2>
              <p className="text-gray-300 leading-relaxed">
                All content, features, and functionality of the Service — including but not limited to the Protego AI platform, risk scoring methodology, software, text, graphics, logos, and data compilations — are owned by Protego AI Inc. or its licensors and are protected by copyright, trademark, and other intellectual property laws. Nothing in these Terms grants you any right to use our intellectual property other than as expressly authorized herein.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Disclaimer of Warranties</h2>
              <div className="p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-gray-300 leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  We do not warrant that: (a) the Service will be uninterrupted or error-free; (b) defects will be corrected; (c) the Service is free of viruses or other harmful components; (d) the results or information obtained through the Service will be accurate, reliable, or complete; or (e) the Service will prevent adverse actions by Amazon against your seller account.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  Risk scores, alerts, and analysis provided by our Service are based on available data and our proprietary methodologies. They are educational indicators only and should not be relied upon as guarantees of any specific outcome.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
              <div className="p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-gray-300 leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PROTEGO AI INC. AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, REVENUE, DATA, BUSINESS, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS RELATED TO THE SERVICE EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR (B) ONE HUNDRED DOLLARS ($100).
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Indemnification</h2>
              <p className="text-gray-300 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Protego AI Inc. and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any third-party rights, including Amazon's Selling Policies; or (d) your violation of any applicable law or regulation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Termination</h2>
              <p className="text-gray-300 leading-relaxed">
                We may suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service will immediately cease. Provisions of these Terms that by their nature should survive termination shall survive, including but not limited to intellectual property rights, disclaimers, indemnification, and limitations of liability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Modifications to the Service and Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify or discontinue the Service, temporarily or permanently, at any time with or without notice. We also reserve the right to update these Terms at any time. We will notify you of material changes by posting updated Terms on our website or via email. Your continued use of the Service following any such changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law provisions. Any dispute arising from these Terms or your use of the Service shall be submitted to binding arbitration or resolved in the courts of competent jurisdiction in Ontario, Canada. You and we agree to waive any right to a class action lawsuit or class-wide arbitration.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Relationship of the Parties</h2>
              <p className="text-gray-300 leading-relaxed">
                Nothing in these Terms creates a partnership, joint venture, agency, or employment relationship between you and Protego AI Inc. We are an independent contractor providing a commercial software-as-a-service platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Entire Agreement</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding the Service and supersede all prior agreements, understandings, and representations, whether written or oral, relating to the subject matter hereof.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">16. Contact Us</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="mt-4 p-6 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white font-semibold">Protego AI Inc.</p>
                <p className="text-gray-300 mt-2">Email: <a href="mailto:hello@protegoos.com" className="text-indigo-400 hover:text-indigo-300">hello@protegoos.com</a></p>
                <p className="text-gray-300">Website: <a href="https://section3.ai" className="text-indigo-400 hover:text-indigo-300">https://section3.ai</a></p>
              </div>
            </section>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
            Protego AI Inc. — section3.ai | Terms of Service
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsOfService;
